<template>
  <li class="kds-debate-timeline-row" :class="{ 'kds-debate-timeline-row--live': live }">
    <span class="kds-debate-timeline-row__time">{{ timestamp }}</span>
    <KdsAvatar :segment="segment" :size="32" :speaking="live" />
    <div class="kds-debate-timeline-row__body">
      <div class="kds-debate-timeline-row__meta">
        <slot name="name">
          <span class="kds-debate-timeline-row__name">{{ personaName }}</span>
        </slot>
        <span
          v-if="addressing && addressing.length > 0"
          class="kds-debate-timeline-row__addressing"
        >
          {{ addressingLabel }}
        </span>
        <span class="kds-debate-timeline-row__sep">·</span>
        <span class="kds-debate-timeline-row__code">{{ personaId }}</span>
        <KdsBadge v-if="live" tone="live" class="kds-debate-timeline-row__badge">
          Live
        </KdsBadge>
      </div>
      <p class="kds-debate-timeline-row__text">{{ text }}</p>
    </div>
    <span class="kds-debate-timeline-row__turn">TURN {{ turn }} / {{ totalTurns }}</span>
  </li>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import KdsAvatar from '@/kds/primitives/KdsAvatar.vue';
import KdsBadge from '@/kds/primitives/KdsBadge.vue';
import type { PersonaSegment } from '@/kds/types';

interface Props {
  timestamp: string;
  segment: PersonaSegment;
  personaName: string;
  personaId: string;
  text: string;
  turn: number;
  totalTurns: number;
  live?: boolean;
  /** Resolved persona names being addressed in this turn ("→ name1, name2"). */
  addressing?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  live: false,
  addressing: undefined
});

/** Formatted addressing label, rendered only when addressing is non-empty. */
const addressingLabel = computed<string>(() => {
  if (!props.addressing || props.addressing.length === 0) return '';
  return `→ ${props.addressing.join(', ')}`;
});
</script>

<style scoped lang="scss">
.kds-debate-timeline-row {
  display: grid;
  grid-template-columns: auto 32px 1fr auto;
  align-items: start;
  gap: var(--space-4);
  padding: var(--space-4) 0;
  border-bottom: var(--border);

  &--live {
    background: var(--signal-tint);
    margin: 0 calc(var(--space-4) * -1);
    padding-inline: var(--space-4);
    border-radius: var(--r-3);
    border-bottom: none;
  }

  &__time,
  &__turn,
  &__code {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone);
  }

  &__time {
    padding-top: var(--space-1);
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-width: 0;
  }

  &__meta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  &__name {
    color: var(--ink);
    font-size: var(--fs-body-sm);
    font-weight: var(--fw-medium);
  }

  &__addressing {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone);
  }

  &__sep {
    color: var(--stone-2);
  }

  &__text {
    margin: 0;
    color: var(--ink);
    font-size: var(--fs-body);
    line-height: var(--lh-body);
  }

  &__turn {
    padding-top: var(--space-1);
  }
}
</style>
